import { useState, useEffect } from "react";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import { Form, Formik, FormikProps } from "formik";
import { Grid, Icon, IconButton } from "@mui/material";
import FormField from "../../../components/FormField";
import FaiButton from "../../../MuiComponents/FaiButton";
import FaiSelect from "../../../MuiComponents/FaiSelect";
import { merchantAuthController } from "../../../controllers/authController";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../../context/ProfileContext";
import { toast } from "react-toastify";

interface InitialObject {
	bankName: string;
	accountNo: string;
	accountHolderName: string;
	ifscCode: string;
	accountType: string;
	isDraft: boolean;
}

function BankDetails() {
	const { typography, palette, borders } = theme;
	const { profile, setProfile } = useProfile();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// const [success, setSuccess] = useState(false);
	const navigate = useNavigate();
	const [initialObject, setInitialObject] = useState<InitialObject>({
		bankName: profile?.bankDetails?.bankName || "",
		accountNo: profile?.bankDetails?.bankAccountNumber || "",
		accountHolderName: profile?.bankDetails?.accountHolderName || "",
		ifscCode: profile?.bankDetails?.ifscCode || "",
		accountType: profile?.bankDetails?.accountType || "",
		isDraft: false,
	});

	// Update form values when profile is loaded/changed
	useEffect(() => {
		if (profile?.bankDetails) {
			setInitialObject({
				bankName: profile.bankDetails.bankName || "",
				accountNo: profile.bankDetails.bankAccountNumber || "",
				accountHolderName: profile.bankDetails.accountHolderName || "",
				ifscCode: profile.bankDetails.ifscCode || "",
				accountType: profile.bankDetails.accountType || "",
				isDraft: false,
			});
		}
	}, [profile]);

	const handleSubmit = async (
		values: InitialObject,
		{ setSubmitting }: any
	) => {
		try {
			setLoading(true);
			setError(null);

			// Transform the form values to match the API requirements
			const apiData = {
				isDraft: values.isDraft,
				bankName: values.bankName,
				ifscCode: values.ifscCode,
				accountType:
					typeof values.accountType === "object" && values.accountType
						? (values.accountType as { value: string }).value
						: values.accountType || "",
				bankAccountNumber: values.accountNo,
				accountHolderName: values.accountHolderName,
			};

			// Call the API with the transformed data
			await merchantAuthController.setBankDetails(apiData);

			// Update the profile context with the new bank details
			setProfile({
				...profile,
				bankDetails: {
					bankName: values.bankName,
					bankAccountNumber: values.accountNo,
					accountHolderName: values.accountHolderName,
					ifscCode: values.ifscCode,
					accountType: typeof values.accountType === "object" && values.accountType
						? (values.accountType as { value: string }).value
						: values.accountType || "",
				},
			});

			// setSuccess(true);
			toast.info("Bank details updated successfully!");
			if (!profile?.bankDetails) {
				navigate("/app/onboarding/brandDetails");
			}
		} catch (err: any) {
			setError(err.response?.data?.message || "Failed to update bank details");
		} finally {
			setLoading(false);
			setSubmitting(false);
		}
	};

	return (
		<>
			<FaiBox>
				<FaiBox
					bgColor={palette.white.main}
					className={"coupon-form-content"}
					mt={"15px"}
					p={"15px"}
					borderRadius={borders.borderRadius.md}
				>
					<FaiBox>
						<FaiTypography
							variant="h4"
							fontWeight={typography.fontWeightMedium}
						>
							Bank Details
							<IconButton>
								<Icon sx={{ color: "black !important" }}>info</Icon>
							</IconButton>
						</FaiTypography>
						<FaiTypography variant="h6" mb={"21px"}>
							Mandatory informations
						</FaiTypography>
					</FaiBox>

					<Formik
						initialValues={initialObject}
						enableReinitialize={true}
						validateOnBlur={true}
						validateOnChange={true}
						onSubmit={handleSubmit}
					>
						{(props: FormikProps<InitialObject>) => {
							const {
								handleSubmit,
								setFieldValue,
								handleChange,
								handleBlur,
								values,
								touched,
								isValid,
								errors,
							} = props;
							return (
								<Form>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Account Holder Name"}
													name={"accountHolderName"}
													placeholder={"Enter Account Holder Name"}
													value={values.accountHolderName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={
														touched.accountHolderName &&
														Boolean(errors.accountHolderName)
													}
													helperText={
														touched.accountHolderName &&
														errors.accountHolderName
													}
												/>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Account Number"}
													name={"accountNo"}
													placeholder={"Enter Bank Account Number"}
													value={values.accountNo}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.accountNo && Boolean(errors.accountNo)}
													helperText={touched.accountNo && errors.accountNo}
												/>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiTypography
												component="label"
												variant="caption"
												fontWeight="bold"
												textTransform="capitalize"
											>
												Account Type
											</FaiTypography>
											<FaiSelect
												placeholder={"Account Type"}
												name={"accountType"}
												value={[
													{ value: "Savings", label: "Savings" },
													{ value: "Current", label: "Current" },
												].find((option) => option.value === values.accountType)}
												onChange={(value) => {
													// Get the label from the selected option
													const selectedOption = [
														{ value: "Savings", label: "Savings" },
														{ value: "Current", label: "Current" },
													].find((option) => option.value === value);

													// Set the value to the label (capitalized)
													setFieldValue(
														"accountType",
														selectedOption ? selectedOption.label : value
													);
												}}
												options={[
													{ value: "Savings", label: "Savings" },
													{ value: "Current", label: "Current" },
												]}
											/>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Bank Name"}
													name={"bankName"}
													placeholder={"Enter Bank Name"}
													value={values.bankName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.bankName && Boolean(errors.bankName)}
													helperText={touched.bankName && errors.bankName}
												/>
											</FaiBox>
										</Grid>

										{/* <Grid item sm={12} xs={12}>
											<FaiBox mb={1}>
												<FormField
													multiline
													rows={5}
													label={"Bank address"}
													name={"bankAddress"}
													placeholder={"Enter Bank Address"}
												/>
											</FaiBox>
										</Grid> */}

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"IFSC Code"}
													name={"ifscCode"}
													placeholder={"Enter IFSC CODE"}
													value={values.ifscCode}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.ifscCode && Boolean(errors.ifscCode)}
													helperText={touched.ifscCode && errors.ifscCode}
												/>
											</FaiBox>
										</Grid>
									</Grid>

									{error && (
										<FaiTypography color="error" mt={2}>
											{error}
										</FaiTypography>
									)}

									{/* {success && (
										<FaiTypography color="success" mt={2}>
											Bank details updated successfully!
										</FaiTypography>
									)} */}

									<FaiBox pt={"15px"}>
										<Grid container justifyContent="flex-end" spacing={2}>
											<Grid item sm={2} xs={12}></Grid>
											<Grid item sm={2} xs={12}>
												<FaiButton
													color="primary"
													variant={"contained"}
													fullWidth
													type="submit"
													disabled={loading}
												>
													{loading
														? "Saving..."
														: profile?.bankDetails
															? "Submit"
															: "Next"}
												</FaiButton>
											</Grid>
										</Grid>
									</FaiBox>
								</Form>
							);
						}}
					</Formik>
				</FaiBox>
			</FaiBox>
		</>
	);
}
export default BankDetails;